import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Empty, List, Skeleton, Space, Tag, Typography } from "antd";
import { CameraOutlined, FilePdfOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { listDocs, photoUrl } from "./api";
import ReportDialog from "./ReportDialog";
import "./siteDocs.css";

const LIST = "/user/site-docs";
const query = params =>
  new URLSearchParams(Object.entries(params).filter(([, value]) => value)).toString();

/**
 * The site documentation of one project, offer or invoice, for its page: the latest entries,
 * a new entry already assigned to it, everything in the list, and a photo report.
 *
 * filter: { project } | { offer } | { invoice }; client: the customer id (prefills new entries);
 * customerEmail and reportTitle prefill the report.
 */
const SiteDocsPanel = ({ filter, client, customerEmail, reportTitle }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [result, setResult] = useState(null);
  const [denied, setDenied] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const key = JSON.stringify(filter);

  useEffect(() => {
    let cancelled = false;
    listDocs({ ...filter })
      .then(res => !cancelled && setResult(res.data))
      .catch(error => {
        if (cancelled) return;
        if (error?.response?.status === 403) setDenied(true);
        else generalErrorAlert(error);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  if (denied) return null;
  if (!result) return <Skeleton active paragraph={{ rows: 3 }} />;

  const newUrl = `${LIST}/new?${query({ client, ...filter })}`;
  const allUrl = `${LIST}?${query(filter)}`;

  return (
    <div className="site-docs-page sd-panel">
      <Space wrap className="sd-panel-actions">
        {result.can?.create && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push(newUrl)}>
            {t("New documentation")}
          </Button>
        )}
        {result.total > 0 && (
          <Button icon={<FilePdfOutlined />} onClick={() => setReportOpen(true)}>
            {t("Photo report (PDF)")}
          </Button>
        )}
        {result.total > result.data.length && (
          <Link to={allUrl}>
            {t("Show all {{n}}", { n: result.total })} <RightOutlined />
          </Link>
        )}
      </Space>

      {result.data.length ? (
        <List
          className="sd-panel-list"
          dataSource={result.data}
          renderItem={item => (
            <List.Item
              onClick={() => history.push(`${LIST}/${item._id}`)}
              className="sd-panel-item"
            >
              <List.Item.Meta
                avatar={
                  item.cover ? (
                    <img
                      className="sd-cover"
                      src={photoUrl(item._id, item.cover)}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <span className="sd-cover sd-cover--empty" aria-hidden="true">
                      <CameraOutlined />
                    </span>
                  )
                }
                title={
                  <Space size={6} wrap>
                    <Typography.Text strong>{item.title || item.number}</Typography.Text>
                    <Typography.Text type="secondary">
                      {item.date ? dayjs(item.date).format("DD.MM.YYYY") : ""}
                    </Typography.Text>
                    <Tag icon={<CameraOutlined />} className="sd-tag">
                      {item.photoCount}
                    </Tag>
                  </Space>
                }
                description={
                  <>
                    <div className="sd-clamp">{item.notes}</div>
                    {item.createdByName && (
                      <div className="sd-small">
                        {t("by {{name}}", { name: item.createdByName })}
                      </div>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("No documentation yet. Photos and notes from the site appear here.")}
        />
      )}

      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        selection={{ filters: filter }}
        count={result.total}
        defaultTitle={reportTitle}
        defaultEmail={customerEmail}
        canEmail={result.can?.email}
      />
    </div>
  );
};

export default SiteDocsPanel;
